import React, { useState, useCallback, useEffect } from "react";
import Swal from "sweetalert2";
import { useNavigate, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import PageTitle from "../../../layouts/PageTitle";
import { createWorkProgressApi } from "../../../../services/workprogressService";
import { useGetWorkProgressDropdownsQuery } from "../../../../store/appApi";
import { selectActiveProjectId, selectActiveProjectName, setFormDirty } from "../../../../store/projectSlice";
import { selectHasModuleAccess } from "../../../../store/authSlice";

// ── Constants ─────────────────────────────────────────────────────────────────
const LABOUR_TYPES = ["Mistry", "Baildar", "Culi"];

const EMPTY_LABOUR_LINE = { type: "", count: "" };
const EMPTY_WORK = { typeOfWork: "", labour: [{ ...EMPTY_LABOUR_LINE }] };
const EMPTY_CONTRACTOR = {
  contractorId: "",
  skilledLabour: "",
  unskilledLabour: "",
  workAssigned: "",
};
const EMPTY_EMPLOYEE = { employeeId: "", locationName: "" };
const EMPTY_MATERIAL = {
  materialId: "",
  qtyIssued: "",
  issuedToContractorId: "",
};

const INITIAL_FORM = {
  reportType: "",
  reportDateTime: "",
  equipmentSelections: {}, // { [equipmentId]: boolean } — which equipment was deployed today
  workExecution: [{ ...EMPTY_WORK }],
  contractorDetails: [{ ...EMPTY_CONTRACTOR }],
  employeeDetails: [{ ...EMPTY_EMPLOYEE }],
  materialDetails: [{ ...EMPTY_MATERIAL }],
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const cloneRow = (template) => ({ ...template });

const AddWorkProgress = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const activeProjectId = useSelector(selectActiveProjectId);
  const activeProjectName = useSelector(selectActiveProjectName);
  const hasWpWrite = useSelector(selectHasModuleAccess("workProgress", "read-write"));
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // ── Dropdown data ─────────────────────────────────────────────────────────
  const { data: dropdowns, isLoading: dropdownsLoading } =
    useGetWorkProgressDropdownsQuery();

  const equipments = dropdowns?.equipments ?? [];
  const contractors = dropdowns?.contractors ?? [];
  const staff = (dropdowns?.staff ?? []).filter(
    (s) => s.role?.toLowerCase() !== "super-admin",
  );
  const materials = dropdowns?.materials ?? [];

  // ── Generic scalar field change ───────────────────────────────────────────
  const handleField = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // ── Dynamic array helpers ─────────────────────────────────────────────────
  const handleArrayField = useCallback((section, index, field, value) => {
    setForm((prev) => {
      const next = prev[section].map((row, i) =>
        i === index ? { ...row, [field]: value } : row,
      );
      return { ...prev, [section]: next };
    });
  }, []);

  const addRow = useCallback((section, template) => {
    setForm((prev) => ({
      ...prev,
      [section]: [...prev[section], cloneRow(template)],
    }));
  }, []);

  const removeRow = useCallback((section, index) => {
    setForm((prev) => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index),
    }));
  }, []);

  // ── Equipment checkbox selection ──────────────────────────────────────────
  const toggleEquipment = useCallback((equipmentId) => {
    setForm((prev) => ({
      ...prev,
      equipmentSelections: {
        ...prev.equipmentSelections,
        [equipmentId]: !prev.equipmentSelections[equipmentId],
      },
    }));
  }, []);

  // ── Labour multi-select (per work row: same "Type of Work", multiple labour+qty lines) ──
  const handleLabourField = useCallback((rowIndex, labourIndex, field, value) => {
    setForm((prev) => ({
      ...prev,
      workExecution: prev.workExecution.map((r, i) =>
        i === rowIndex
          ? {
              ...r,
              labour: r.labour.map((l, li) =>
                li === labourIndex ? { ...l, [field]: value } : l,
              ),
            }
          : r,
      ),
    }));
  }, []);

  const addLabourLine = useCallback((rowIndex) => {
    setForm((prev) => ({
      ...prev,
      workExecution: prev.workExecution.map((r, i) =>
        i === rowIndex
          ? { ...r, labour: [...r.labour, { ...EMPTY_LABOUR_LINE }] }
          : r,
      ),
    }));
  }, []);

  const removeLabourLine = useCallback((rowIndex, labourIndex) => {
    setForm((prev) => ({
      ...prev,
      workExecution: prev.workExecution.map((r, i) =>
        i === rowIndex
          ? { ...r, labour: r.labour.filter((_, li) => li !== labourIndex) }
          : r,
      ),
    }));
  }, []);

  // ── Material stock guard ──────────────────────────────────────────────────
  // Returns the available balance for a given materialId
  const balanceFor = (materialId) =>
    materials.find((m) => m._id === materialId)?.currentBalance ?? null;

  const materialError = (row) => {
    if (!row.materialId || !row.qtyIssued) return null;
    const available = balanceFor(row.materialId);
    if (available === null) return null;
    if (Number(row.qtyIssued) > available)
      return `Exceeds available stock (${available} units).`;
    return null;
  };

  // Track dirty state so the header switcher can warn on unsaved changes
  useEffect(() => {
    const hasInput =
      form.reportType ||
      form.reportDateTime ||
      Object.values(form.equipmentSelections).some(Boolean) ||
      form.workExecution.some((r) => r.typeOfWork || r.labour.some((l) => l.type)) ||
      form.contractorDetails.some((r) => r.contractorId) ||
      form.employeeDetails.some((r) => r.employeeId) ||
      form.materialDetails.some((r) => r.materialId);
    dispatch(setFormDirty(!!hasInput));
    return () => dispatch(setFormDirty(false));
  }, [form, dispatch]);

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!activeProjectId) e.projectId = "No active project selected. Please select a project from the dashboard.";
    if (!form.reportType) e.reportType = "Report type is required.";
    if (!form.reportDateTime) e.reportDateTime = "Date & time is required.";

    // Block if any material row has a quantity violation
    const hasStockError = form.materialDetails.some(
      (row) => materialError(row) !== null,
    );
    if (hasStockError)
      e.materialDetails = "Fix quantity errors before submitting.";

    return e;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: Object.values(validationErrors)[0],
      });
      return;
    }

    const payload = {
      projectId: activeProjectId,
      reportType: form.reportType,
      reportDateTime: form.reportDateTime,

      equipments: Object.entries(form.equipmentSelections)
        .filter(([, selected]) => selected)
        .map(([equipmentId]) => ({ equipmentId, eqpQuantity: 1 })),

      workExecution: form.workExecution
        .filter((r) => r.typeOfWork || r.labour.some((l) => l.type && l.count))
        .flatMap((r) => {
          const lines = r.labour.filter((l) => l.type && l.count);
          return lines.length
            ? lines.map((l) => ({
                typeOfWork: r.typeOfWork,
                labourEngaged: l.type,
                numberOfLabour: Number(l.count),
              }))
            : [{ typeOfWork: r.typeOfWork, labourEngaged: "", numberOfLabour: 0 }];
        }),

      contractorDetails: form.contractorDetails
        .filter((r) => r.contractorId)
        .map((r) => ({
          contractorId: r.contractorId,
          skilledLabour: Number(r.skilledLabour),
          unskilledLabour: Number(r.unskilledLabour),
          workAssigned: r.workAssigned,
        })),

      employeeDetails: form.employeeDetails
        .filter((r) => r.employeeId)
        .map((r) => ({
          employeeId: r.employeeId,
          locationName: r.locationName,
        })),

      materialDetails: form.materialDetails
        .filter((r) => r.materialId && r.qtyIssued)
        .map((r) => ({
          materialId: r.materialId,
          qtyIssued: Number(r.qtyIssued),
          issuedToContractorId: r.issuedToContractorId || undefined,
        })),
    };

    try {
      setSubmitting(true);
      await createWorkProgressApi(payload);
      Swal.fire({
        icon: "success",
        title: "Report Saved!",
        text: "Daily progress report has been submitted successfully.",
        confirmButtonText: "OK",
      }).then(() => navigate("/progress/workprogress/list"));
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text:
          err.response?.data?.message ??
          "Something went wrong. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasWpWrite) return <Navigate to="../list" replace />;

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (dropdownsLoading) {
    return (
      <>
        <PageTitle activeMenu="Add Work Progress" motherMenu="Daily Progress" />
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
          <p className="mt-2 text-muted">Loading form data…</p>
        </div>
      </>
    );
  }

  // ── Section header helper ─────────────────────────────────────────────────
  const SectionHeader = ({ number, title }) => (
    <div className="card-header bg-primary bg-opacity-10 d-flex align-items-center gap-2 py-2">
      <h5 className="mb-0 d-flex align-items-center gap-2" style={{ fontSize: 14, fontWeight: 600 }}>
        <span className="badge bg-primary" style={{ fontSize: 12 }}>
          {number}
        </span>
        <span style={{ color: "#adb5bd" }}>{title}</span>
      </h5>
    </div>
  );

  return (
    <>
      <PageTitle
        activeMenu="Today Work in Progress"
        motherMenu="Daily Progress"
      />

      <form onSubmit={handleSubmit} noValidate>
        {/* ── Section 1: Core Meta ──────────────────────────────────────── */}
        <div className="card mb-4">
          <SectionHeader number={1} title="Core Report Details" />
          <div className="card-body">
            <div className="row g-3">
              {/* Active Project (read-only — set via header project switcher) */}
              <div className="col-md-4">
                <label className="form-label">Project</label>
                {activeProjectName ? (
                  <div
                    className="form-control d-flex align-items-center gap-2"
                    style={{ background: "#3243360d", border: "1px solid #32433640", color: "#324336", fontWeight: 600 }}
                  >
                    <i className="fas fa-building" style={{ fontSize: 14 }} />
                    {activeProjectName}
                  </div>
                ) : (
                  <div className="alert alert-warning py-2 mb-0" style={{ fontSize: 13 }}>
                    No project selected. <a href="/dashboard">Select a project</a> first.
                  </div>
                )}
                {errors.projectId && (
                  <small className="text-danger d-block mt-1">{errors.projectId}</small>
                )}
              </div>

              {/* Report Type */}
              <div className="col-md-4">
                <label className="form-label">
                  Shift <span className="text-danger">*</span>
                </label>
                <select
                  name="reportType"
                  className={`form-control${errors.reportType ? " is-invalid" : ""}`}
                  value={form.reportType}
                  onChange={handleField}
                >
                  <option value="">Select Shift</option>
                  <option value="morning">Morning</option>
                  <option value="evening">Evening</option>
                </select>
                {errors.reportType && (
                  <div className="invalid-feedback">{errors.reportType}</div>
                )}
              </div>

              {/* Date & Time */}
              <div className="col-md-4">
                <label className="form-label">
                  Report Date &amp; Time <span className="text-danger">*</span>
                </label>
                <input
                  type="datetime-local"
                  name="reportDateTime"
                  className={`form-control${errors.reportDateTime ? " is-invalid" : ""}`}
                  value={form.reportDateTime}
                  onChange={handleField}
                />
                {errors.reportDateTime && (
                  <div className="invalid-feedback">
                    {errors.reportDateTime}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Section 2a: Equipment Deployment ─────────────────────────── */}
        <div className="card mb-4">
          <SectionHeader number="2A" title="Equipment Deployment" />
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-bordered align-middle mb-2">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 50 }} />
                    <th>Equipment</th>
                  </tr>
                </thead>
                <tbody>
                  {equipments.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="text-center text-muted py-3">
                        No equipment registered for this project.
                      </td>
                    </tr>
                  ) : (
                    equipments.map((eq) => {
                      const selected = !!form.equipmentSelections[eq._id];
                      return (
                        <tr key={eq._id}>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selected}
                              onChange={() => toggleEquipment(eq._id)}
                            />
                          </td>
                          <td>
                            {eq.eqpName}
                            {eq.eqpType ? ` — ${eq.eqpType}` : ""}
                            {eq.eqpNumber ? ` (${eq.eqpNumber})` : ""}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ── Section 2b: Work Execution ────────────────────────────────── */}
        <div className="card mb-4">
          <SectionHeader number="2B" title="Work Execution &amp; Manpower" />
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-bordered align-middle mb-2">
                <thead className="table-light">
                  <tr>
                    <th style={{ minWidth: 200 }}>Type of Work</th>
                    <th style={{ minWidth: 340 }}>Labour Engaged &amp; Qty</th>
                    <th style={{ width: 50 }} />
                  </tr>
                </thead>
                <tbody>
                  {form.workExecution.map((row, i) => {
                    return (
                      <tr key={i}>
                        <td>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={row.typeOfWork}
                            onChange={(e) =>
                              handleArrayField(
                                "workExecution",
                                i,
                                "typeOfWork",
                                e.target.value,
                              )
                            }
                            placeholder="e.g. Boundary Wall"
                          />
                        </td>
                        <td>
                          <table className="table table-sm mb-2">
                            <thead>
                              <tr>
                                <th style={{ fontSize: 12 }}>Labour Engaged</th>
                                <th style={{ fontSize: 12, width: 100 }}>Qty</th>
                                <th style={{ width: 36 }} />
                              </tr>
                            </thead>
                            <tbody>
                              {row.labour.map((l, li) => {
                                const availableTypes = LABOUR_TYPES.filter(
                                  (lt) =>
                                    lt === l.type ||
                                    !row.labour.some((o) => o.type === lt),
                                );
                                return (
                                  <tr key={li}>
                                    <td>
                                      <select
                                        className="form-control form-control-sm"
                                        value={l.type}
                                        onChange={(e) =>
                                          handleLabourField(
                                            i,
                                            li,
                                            "type",
                                            e.target.value,
                                          )
                                        }
                                      >
                                        <option value="">Select Labour</option>
                                        {availableTypes.map((lt) => (
                                          <option key={lt} value={lt}>
                                            {lt}
                                          </option>
                                        ))}
                                      </select>
                                    </td>
                                    <td>
                                      <input
                                        type="number"
                                        min="0"
                                        className="form-control form-control-sm"
                                        value={l.count}
                                        onChange={(e) =>
                                          handleLabourField(
                                            i,
                                            li,
                                            "count",
                                            e.target.value,
                                          )
                                        }
                                        placeholder="0"
                                      />
                                    </td>
                                    <td className="text-center">
                                      {row.labour.length > 1 && (
                                        <button
                                          type="button"
                                          className="btn btn-danger btn-xs py-0 px-1"
                                          onClick={() =>
                                            removeLabourLine(i, li)
                                          }
                                        >
                                          &times;
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => addLabourLine(i)}
                          >
                            + Add Labour
                          </button>
                        </td>
                        <td className="text-center">
                          {form.workExecution.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-danger btn-xs py-0 px-1"
                              onClick={() => removeRow("workExecution", i)}
                            >
                              &times;
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => addRow("workExecution", EMPTY_WORK)}
            >
              + Add Work Row
            </button>
          </div>
        </div>

        {/* ── Section 3: Contractor Engagement ─────────────────────────── */}
        <div className="card mb-4">
          <SectionHeader number={3} title="Contractor Engagement" />
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-bordered align-middle mb-2">
                <thead className="table-light">
                  <tr>
                    <th>Contractor</th>
                    <th style={{ width: 130 }}>Skilled Labour</th>
                    <th style={{ width: 140 }}>Unskilled Labour</th>
                    <th>Work Assigned</th>
                    <th style={{ width: 50 }} />
                  </tr>
                </thead>
                <tbody>
                  {form.contractorDetails.map((row, i) => (
                    <tr key={i}>
                      <td>
                        <select
                          className="form-control form-control-sm"
                          value={row.contractorId}
                          onChange={(e) =>
                            handleArrayField(
                              "contractorDetails",
                              i,
                              "contractorId",
                              e.target.value,
                            )
                          }
                        >
                          <option value="">Select Contractor</option>
                          {contractors.map((c) => (
                            <option key={c._id} value={c._id}>
                              {c.name}
                              {c.phone ? ` — ${c.phone}` : ""}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          className="form-control form-control-sm"
                          value={row.skilledLabour}
                          onChange={(e) =>
                            handleArrayField(
                              "contractorDetails",
                              i,
                              "skilledLabour",
                              e.target.value,
                            )
                          }
                          placeholder="0"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          className="form-control form-control-sm"
                          value={row.unskilledLabour}
                          onChange={(e) =>
                            handleArrayField(
                              "contractorDetails",
                              i,
                              "unskilledLabour",
                              e.target.value,
                            )
                          }
                          placeholder="0"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={row.workAssigned}
                          onChange={(e) =>
                            handleArrayField(
                              "contractorDetails",
                              i,
                              "workAssigned",
                              e.target.value,
                            )
                          }
                          placeholder="Describe work assigned"
                        />
                      </td>
                      <td className="text-center">
                        {form.contractorDetails.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-danger btn-xs py-0 px-1"
                            onClick={() => removeRow("contractorDetails", i)}
                          >
                            &times;
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => addRow("contractorDetails", EMPTY_CONTRACTOR)}
            >
              + Add Contractor Row
            </button>
          </div>
        </div>

        {/* ── Section 4: Internal Staff Deployment ─────────────────────── */}
        <div className="card mb-4">
          <SectionHeader number={4} title="Internal Staff Deployment" />
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-bordered align-middle mb-2">
                <thead className="table-light">
                  <tr>
                    <th>Employee (Name — Role)</th>
                    <th>Location / Zone</th>
                    <th style={{ width: 50 }} />
                  </tr>
                </thead>
                <tbody>
                  {form.employeeDetails.map((row, i) => (
                    <tr key={i}>
                      <td>
                        <select
                          className="form-control form-control-sm"
                          value={row.employeeId}
                          onChange={(e) =>
                            handleArrayField(
                              "employeeDetails",
                              i,
                              "employeeId",
                              e.target.value,
                            )
                          }
                        >
                          <option value="">Select Employee</option>
                          {staff.map((s) => (
                            <option key={s._id} value={s._id}>
                              {s.name}
                              {s.role ? ` — ${s.role}` : ""}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={row.locationName}
                          onChange={(e) =>
                            handleArrayField(
                              "employeeDetails",
                              i,
                              "locationName",
                              e.target.value,
                            )
                          }
                          placeholder="e.g. Block A – 3rd Floor"
                        />
                      </td>
                      <td className="text-center">
                        {form.employeeDetails.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-danger btn-xs py-0 px-1"
                            onClick={() => removeRow("employeeDetails", i)}
                          >
                            &times;
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => addRow("employeeDetails", EMPTY_EMPLOYEE)}
            >
              + Add Staff Row
            </button>
          </div>
        </div>

        {/* ── Section 5: Material Issuance ─────────────────────────────── */}
        <div className="card mb-4">
          <SectionHeader
            number={5}
            title="Material Issuance &amp; Stock Allocation"
          />
          <div className="card-body">
            {errors.materialDetails && (
              <div className="alert alert-danger py-2 mb-3">
                {errors.materialDetails}
              </div>
            )}
            <div className="table-responsive">
              <table className="table table-bordered align-middle mb-2">
                <thead className="table-light">
                  <tr>
                    <th>Material (Available Stock)</th>
                    <th style={{ width: 150 }}>Qty Issued</th>
                    <th>Issued To (Contractor)</th>
                    <th style={{ width: 50 }} />
                  </tr>
                </thead>
                <tbody>
                  {form.materialDetails.map((row, i) => {
                    const stockErr = materialError(row);
                    const selectedMat = materials.find(
                      (m) => m._id === row.materialId,
                    );
                    return (
                      <tr key={i}>
                        <td>
                          <select
                            className="form-control form-control-sm"
                            value={row.materialId}
                            onChange={(e) =>
                              handleArrayField(
                                "materialDetails",
                                i,
                                "materialId",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">Select Material</option>
                            {materials.map((m) => (
                              <option key={m._id} value={m._id}>
                                {m.skuName}
                                {m.measurement ? ` (${m.measurement})` : ""}
                                {" — "}
                                <span>{m.currentBalance} available</span>
                              </option>
                            ))}
                          </select>
                          {selectedMat && (
                            <small className="text-muted d-block mt-1">
                              Available:{" "}
                              <strong>
                                {selectedMat.currentBalance}{" "}
                                {selectedMat.measurement}
                              </strong>
                            </small>
                          )}
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            className={`form-control form-control-sm${stockErr ? " is-invalid" : ""}`}
                            value={row.qtyIssued}
                            onChange={(e) =>
                              handleArrayField(
                                "materialDetails",
                                i,
                                "qtyIssued",
                                e.target.value,
                              )
                            }
                            placeholder="0"
                          />
                          {stockErr && (
                            <div
                              className="text-danger"
                              style={{ fontSize: "0.75rem", marginTop: 3 }}
                            >
                              ⚠ {stockErr}
                            </div>
                          )}
                        </td>
                        <td>
                          <select
                            className="form-control form-control-sm"
                            value={row.issuedToContractorId}
                            onChange={(e) =>
                              handleArrayField(
                                "materialDetails",
                                i,
                                "issuedToContractorId",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">
                              Select Contractor (optional)
                            </option>
                            {contractors.map((c) => (
                              <option key={c._id} value={c._id}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="text-center">
                          {form.materialDetails.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-danger btn-xs py-0 px-1"
                              onClick={() => removeRow("materialDetails", i)}
                            >
                              &times;
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => addRow("materialDetails", EMPTY_MATERIAL)}
            >
              + Add Material Row
            </button>
          </div>
        </div>

        {/* ── Submit ─────────────────────────────────────────────────────── */}
        <div className="d-flex gap-2 mb-5">
          <button
            type="submit"
            className="btn btn-primary px-4"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Saving Report…
              </>
            ) : (
              "Submit Daily Progress Report"
            )}
          </button>
          <button
            type="button"
            className="btn btn-light px-4"
            onClick={() => navigate(-1)}
            disabled={submitting}
          >
            Cancel
          </button>
        </div>
      </form>
    </>
  );
};

export default AddWorkProgress;
